/* eslint-disable @typescript-eslint/no-base-to-string, react-refresh/only-export-components */
import { memo, useMemo, useState, type ReactNode } from "react";
import type {
  AbilityRecord,
  CollectionManifest,
  ControlCommand,
  FieldManifest,
  LayoutKind,
  Mode,
  SlotKind,
  ViewControl,
  ViewElement,
  ViewManifest,
  ViewSection,
  WidgetKind,
} from "../../shared/api/generated/contracts";
import {
  ELEMENT_KINDS,
  LAYOUTS,
  WIDGET_KINDS,
} from "../../shared/api/generated/contracts";
import { useT, type Translate } from "../../shared/i18n";
import {
  Button,
  Card,
  Divider,
  EmptyState,
  Table,
  Tabs,
  type TableColumn,
} from "../../shared/ui";
import { commandOfButton, isCommandAvailable, runCommand } from "./commands";
import { FieldControl, fieldModeFor, visibleFields } from "./fields";
import { MetricWidget } from "./MetricWidget";
import { RecordList } from "./RecordList";
import { ActiveFab, HeaderActions } from "./ViewControls";
export interface RendererContext {
  abilityId?: string;
  viewId?: string;
  /** Collection declared by the current record-context view. */
  collectionId?: string;
  mode: Mode;
  records: AbilityRecord[];
  navigate: (target: string) => void;
  /**
   * Host hook for commands the renderer cannot run itself (create_record,
   * print). Controls that need it are not rendered while it is absent.
   */
  onCommand?: (command: ControlCommand) => void;
  /** Field manifests, so a `field` element can use the render dictionary. */
  collections?: CollectionManifest[];
  fieldIndex?: ReadonlyMap<string, FieldManifest>;
}
type WidgetRenderer = (
  element: ViewElement,
  context: RendererContext,
  t: Translate,
) => ReactNode;

/** Values are user content: rendered as text, never interpreted as markup. */
function collectionFor(
  context: RendererContext,
  collectionId: string | undefined,
): CollectionManifest | undefined {
  return context.collections?.find(
    (collection) => collection.id === collectionId,
  );
}

const renderList: WidgetRenderer = (element, context) => (
  <RecordList element={element} context={context} />
);
const renderMetric: WidgetRenderer = (element, context) => (
  <MetricWidget element={element} context={context} />
);
const renderDetail: WidgetRenderer = (_element, context, t) => (
  <Card title={t("widget.details")}>
    <p className="text-body text-fg-muted">
      {context.records.length
        ? t("widget.selectRecord")
        : t("widget.noRecordSelected")}
    </p>
  </Card>
);
const renderForm: WidgetRenderer = (element, context, t) => (
  <Card title={String(element.config?.title ?? t("widget.recordForm"))}>
    <form
      className="flex flex-col items-start gap-3"
      onSubmit={(event) => {
        event.preventDefault();
      }}
    >
      <Button type="submit">{t("record.save")}</Button>
      <small className="text-caption text-fg-muted">
        {context.mode === "preview"
          ? t("widget.previewLocal")
          : t("widget.serverConfirmed")}
      </small>
    </form>
  </Card>
);
const renderTable: WidgetRenderer = (element, context, t) => {
  const configuredCollectionId = element.config?.collection_id;
  const collection = collectionFor(
    context,
    typeof configuredCollectionId === "string"
      ? configuredCollectionId
      : context.collectionId,
  );
  const columns: TableColumn<AbilityRecord>[] = visibleFields(collection).map(
    (field) => ({
      id: field.id,
      header: field.label,
      cell: (record) => (
        <FieldControl
          field={field}
          value={record.payload[field.id]}
          mode={fieldModeFor(context.mode, false)}
        />
      ),
    }),
  );
  return (
    <Card>
      <Table
        caption={t("widget.records")}
        columns={columns}
        rows={context.records}
        rowKey={(record) => record.id}
        empty={<EmptyState title={t("empty.records")} body={t("empty.body")} />}
      />
    </Card>
  );
};
const renderSummary: WidgetRenderer = (_element, context, t) => (
  <Card>
    <p className="flex items-baseline gap-2">
      <strong className="text-h2 text-fg">{context.records.length}</strong>
      <span className="text-body text-fg-muted">{t("widget.recordCount")}</span>
    </p>
  </Card>
);
export const WIDGET_RENDERERS = {
  list: renderList,
  detail: renderDetail,
  form: renderForm,
  table: renderTable,
  summary: renderSummary,
  metric: renderMetric,
} satisfies Record<WidgetKind, WidgetRenderer>;

function renderElement(
  element: ViewElement,
  context: RendererContext,
  t: Translate,
): ReactNode {
  if (!ELEMENT_KINDS.includes(element.kind))
    return <div role="alert">{t("widget.unsupportedElement")}</div>;
  if (element.kind === "widget") {
    const kind = String(element.config?.widget) as WidgetKind;
    if (!WIDGET_KINDS.includes(kind))
      return <div role="alert">{t("widget.unsupportedWidget")}</div>;
    const configuredCollectionId = element.config?.collection_id;
    const collectionId =
      typeof configuredCollectionId === "string"
        ? configuredCollectionId
        : context.collectionId;
    const scoped =
      typeof collectionId === "string"
        ? {
            ...context,
            records: context.records.filter(
              (record) => record.collection_id === collectionId,
            ),
          }
        : context;
    return WIDGET_RENDERERS[kind](element, scoped, t);
  }
  if (element.kind === "heading")
    return (
      <h2 className="text-h3 text-fg">{String(element.config?.text ?? "")}</h2>
    );
  if (element.kind === "text")
    return (
      <p className="text-body text-fg-muted">
        {String(element.config?.text ?? "")}
      </p>
    );
  if (element.kind === "divider") return <Divider />;
  if (element.kind === "button") {
    const command = commandOfButton(element);
    if (!command || !isCommandAvailable(command, context)) return null;
    return (
      <Button
        onClick={() => {
          runCommand(command, context);
        }}
      >
        {String(element.config?.label ?? t("widget.open"))}
      </Button>
    );
  }
  const fieldId = String(element.config?.field_id ?? "");
  const field = context.fieldIndex?.get(fieldId);
  if (!field) return <span className="text-body text-fg-muted">{fieldId}</span>;
  const record = context.collectionId
    ? context.records.find(
        (candidate) => candidate.collection_id === context.collectionId,
      )
    : undefined;
  return (
    <FieldControl
      field={field}
      value={record?.payload[field.id]}
      mode={fieldModeFor(context.mode, false)}
    />
  );
}
function ManifestViewComponent({
  view,
  context,
  hideTitle = false,
  fabAboveActionBar = false,
}: {
  view: ViewManifest;
  context: RendererContext;
  hideTitle?: boolean;
  /** The screen shows a sticky ActionBar the FAB must clear. */
  fabAboveActionBar?: boolean;
}): ReactNode {
  const t = useT();
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const collectionId = view.context.collection_id;
  const fieldIndex = useMemo(
    () =>
      new Map(
        context.collections
          ?.find((collection) => collection.id === collectionId)
          ?.fields.filter((field) => field.hidden !== true)
          .map((field) => [field.id, field] as const) ?? [],
      ),
    [collectionId, context.collections],
  );
  const scopedContext = {
    ...context,
    collectionId,
    viewId: view.id,
    fieldIndex,
  };
  const isTabs = view.layout === "tabs";
  const activeSection = isTabs
    ? (view.sections.find((section) => section.id === activeTab) ??
      view.sections[0])
    : undefined;
  const fab: ViewControl | undefined = activeSection?.fab ?? view.fab;
  const fabShown =
    fab !== undefined && isCommandAvailable(fab.command, context);
  // Keeps the last row clear of the floating button (size + offset + inset).
  const fabClearance = !fabShown
    ? undefined
    : fabAboveActionBar
      ? "pb-[calc(env(safe-area-inset-bottom)+var(--tabbar-h)+5.5rem)]"
      : "pb-[calc(env(safe-area-inset-bottom)+var(--tabbar-h)+0.5rem)]";
  return (
    <section aria-labelledby={`view-${view.id}`} className={fabClearance}>
      {hideTitle ? (
        <>
          <span id={`view-${view.id}`} className="sr-only">
            {view.label}
          </span>
          {!isTabs && (
            <HeaderActions
              actions={view.header_actions}
              context={scopedContext}
              className="mb-4 mt-1 w-full"
            />
          )}
        </>
      ) : (
        <div className="mb-4 flex items-center gap-2">
          <h1 id={`view-${view.id}`} className="min-w-0 flex-1 text-h1 text-fg">
            {view.label}
          </h1>
          <HeaderActions
            actions={view.header_actions}
            context={scopedContext}
          />
        </div>
      )}
      {renderLayout(
        view,
        (section, variant) => (
          <SectionBlock
            key={section.id}
            section={section}
            variant={variant}
            context={scopedContext}
            t={t}
          />
        ),
        {
          value: activeSection?.id,
          onChange: setActiveTab,
          trailing: hideTitle ? (
            <HeaderActions
              compact
              actions={view.header_actions}
              context={scopedContext}
            />
          ) : undefined,
        },
      )}
      <ActiveFab
        control={fab}
        context={scopedContext}
        aboveActionBar={fabAboveActionBar}
      />
    </section>
  );
}

type SectionVariant = "default" | "tab" | "band";

function SectionBlock({
  section,
  variant,
  context,
  t,
}: {
  section: ViewSection;
  variant: SectionVariant;
  context: RendererContext;
  t: Translate;
}): ReactNode {
  const grid =
    variant === "band"
      ? "grid-cols-2 sm:grid-cols-4"
      : section.columns === 2
        ? "grid-cols-1 sm:grid-cols-2"
        : "grid-cols-1";
  return (
    <div className={`mb-5 grid gap-4 ${grid}`}>
      {/* A tab's title is already its trigger label. */}
      {section.title && variant !== "tab" && (
        <h2 className="col-span-full text-h3 text-fg">{section.title}</h2>
      )}
      {section.elements.map((element, index) => (
        <div
          className={
            variant === "band" &&
            section.elements.length % 2 === 1 &&
            index === section.elements.length - 1
              ? "col-span-2 min-w-0 sm:col-span-1"
              : "min-w-0"
          }
          key={element.id}
        >
          {renderElement(element, context, t)}
        </div>
      ))}
    </div>
  );
}

type RenderSection = (
  section: ViewSection,
  variant: SectionVariant,
) => ReactNode;

/** Visual meaning of each layout preset (ADR 0185); sources pick only names. */
function renderLayout(
  view: ViewManifest,
  render: RenderSection,
  tabs: {
    value?: string;
    onChange: (value: string) => void;
    trailing?: ReactNode;
  },
): ReactNode {
  const layoutId: LayoutKind =
    view.layout && view.layout in LAYOUTS ? view.layout : "stack";
  const layout = LAYOUTS[layoutId];
  const inSlot = (slot: SlotKind) =>
    view.sections.filter(
      (section) =>
        (section.slot && section.slot in layout.slots
          ? section.slot
          : layout.defaultSlot) === slot,
    );
  if (layoutId === "tabs")
    return (
      <Tabs
        label={view.label}
        value={tabs.value}
        onValueChange={tabs.onChange}
        trailing={tabs.trailing}
        items={view.sections.map((section) => ({
          value: section.id,
          label: section.title ?? section.id,
          content: render(section, "tab"),
        }))}
      />
    );
  if (layoutId === "dashboard")
    return (
      <>
        {inSlot("summary").map((section) => render(section, "band"))}
        {inSlot("main").map((section) => render(section, "default"))}
      </>
    );
  if (layoutId === "sidebar")
    return (
      <div className="grid grid-cols-1 gap-x-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          {inSlot("main").map((section) => render(section, "default"))}
        </div>
        <aside className="min-w-0">
          {inSlot("aside").map((section) => render(section, "default"))}
        </aside>
      </div>
    );
  return view.sections.map((section) => render(section, "default"));
}
export const ManifestView = memo(ManifestViewComponent);
